import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Plus, Trash2, Settings, Clock, Users, 
  MessageSquare, Target, Zap, AlertCircle, Check, Copy, Sparkles,
  Calendar, Globe, BarChart3, TestTube, Play, Pause, Edit3
} from 'lucide-react';
import { campaignService, instagramAccountService, profileService, campaignMessageService, campaignAccountService, leadsListService, campaignLeadsListService, subscriptionService } from '../lib/supabaseServices';
import { AccountSelection, SchedulingSettings, ReviewAndLaunch, LeadsListSelection } from './CampaignWizardSteps';
import { MessageSequencesFull } from './MessageSequencesFull';
import LeadsImportModal from './LeadsImportModal';

const CampaignEditor = ({ isOpen, onClose, darkMode, campaign, onCampaignUpdated }) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [showLeadsImport, setShowLeadsImport] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalContent, setUpgradeModalContent] = useState({});
  
  // Campaign data state
  const [campaignData, setCampaignData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    industry: '',
    goals: '',
    
    // Step 2: Leads Lists (multiple)
    selectedLeadsLists: [],
    
    // Step 3: Messages & A/B Testing
    messages: {
      opener_a: {
        text: '',
        enabled: true
      },
      opener_b: {
        text: '',
        enabled: false
      },
      followup1: {
        text: '',
        enabled: true,
        delay_days: 3
      },
      followup2: {
        text: '',
        enabled: false,
        delay_days: 7
      },
      followup3: {
        text: '',
        enabled: false,
        delay_days: 14
      }
    },
    ab_test_percentage: 50,
    
    // Step 4: Account Selection
    selectedAccounts: [],
    
    // Step 5: Scheduling & Settings
    send_time_start: '09:00',
    send_time_end: '17:00',
    timezone: 'UTC',
    exclude_weekends: false,
    exclude_holidays: false,
    daily_message_min: 20,
    daily_message_max: 50,
    
    // Advanced settings
    automation_settings: {
      auto_pause_on_high_response: false,
      auto_pause_threshold: 80,
      daily_lead_limit: 100
    }
  });

  // Wizard steps configuration
  const steps = [
    {
      id: 'basic',
      title: 'Info',
      description: 'Basic campaign details and objectives',
      icon: Target
    },
    {
      id: 'leads',
      title: 'Leads',
      description: 'Import or select your leads list',
      icon: Users
    },
    {
      id: 'messages',
      title: 'Messages',
      description: 'Create message variations and follow-ups',
      icon: MessageSquare
    },
    {
      id: 'accounts',
      title: 'Accounts',
      description: 'Select accounts for this campaign',
      icon: Users
    },
    {
      id: 'scheduling',
      title: 'Timing',
      description: 'Configure when messages are sent',
      icon: Clock
    },
    {
      id: 'review',
      title: 'Review',
      description: 'Final review before updating campaign',
      icon: Check
    }
  ];

  // Load initial data when campaign changes
  useEffect(() => {
    if (isOpen && campaign) {
      loadCampaignData();
    }
  }, [isOpen, campaign]);

  const loadCampaignData = async () => {
    try {
      setIsLoading(true);
      
      // Load basic data
      const [profile, accounts] = await Promise.all([
        profileService.getProfile(),
        instagramAccountService.getAccounts()
      ]);
      
      setUserProfile(profile);
      setAvailableAccounts(accounts);

      // Load campaign-specific data
      const [campaignDetails, campaignMessages, campaignAccounts, campaignLeadsLists] = await Promise.all([
        campaignService.getCampaign(campaign.id),
        campaignMessageService.getCampaignMessages(campaign.id),
        campaignAccountService.getCampaignAccounts(campaign.id),
        campaignLeadsListService.getCampaignLeadsLists(campaign.id)
      ]);

      const convertedMessages = convertMessagesToObject(campaignMessages);

      // Populate campaign data
      const newCampaignData = {
        // Basic info
        name: campaignDetails.name || '',
        description: campaignDetails.description || '',
        industry: campaignDetails.industry || '',
        goals: campaignDetails.goals || '',
        
        // Leads lists
        selectedLeadsLists: campaignLeadsLists?.map(cll => cll.leads_lists) || [],
        
        // Messages - use pre-converted structure
        ...convertedMessages,
        
        // Accounts
        selectedAccounts: campaignAccounts?.map(ca => ca.instagram_account_id) || [],
        
        // Scheduling
        send_time_start: campaignDetails.send_time_start?.substring(0, 5) || '09:00',
        send_time_end: campaignDetails.send_time_end?.substring(0, 5) || '17:00',
        timezone: campaignDetails.timezone || 'UTC',
        exclude_weekends: campaignDetails.exclude_weekends || false,
        exclude_holidays: campaignDetails.exclude_holidays || false,
        daily_message_min: campaignDetails.daily_message_min || 20,
        daily_message_max: campaignDetails.daily_message_max || 50,
        
        // Advanced settings
        automation_settings: campaignDetails.automation_settings || {
          auto_pause_on_high_response: false,
          auto_pause_threshold: 80,
          daily_lead_limit: 100
        }
      };
      
      setCampaignData(newCampaignData);
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Convert messages array to object structure expected by the UI
  const convertMessagesToObject = (messagesArray) => {
    
    if (!messagesArray || messagesArray.length === 0) {
      // Return default structure with one sequence
      return {
        messageSequences: [{
          id: 'sequence_0',
          name: 'Sequence A',
          enabled: true,
          opener: {
            id: 'opener_0',
            text: '',
            enabled: true
          },
          followups: []
        }]
      };
    }

    // Group messages by sequence (A, B, etc.)
    const sequences = {};
    const sequenceIndexMap = {}; // Map sequence letters to numeric indices
    let sequenceCount = 0;
    
    // First pass: collect all openers to establish sequences
    messagesArray.forEach(msg => {
      if (msg.message_type.startsWith('opener_')) {
        const sequenceId = msg.message_type.replace('opener_', '') || 'a';
        
        // Assign numeric index if not already assigned
        if (!(sequenceId in sequenceIndexMap)) {
          sequenceIndexMap[sequenceId] = sequenceCount++;
        }
        
        sequences[sequenceId] = {
          id: `sequence_${sequenceIndexMap[sequenceId]}`,
          name: `Sequence ${sequenceId.toUpperCase()}`,
          enabled: true,
          opener: {
            id: `opener_${sequenceIndexMap[sequenceId]}`,
            text: msg.message_text || '',
            enabled: msg.is_active !== false
          },
          followups: []
        };
      }
    });
    
    // If no openers found but we have messages, create default sequence
    if (Object.keys(sequences).length === 0 && messagesArray.length > 0) {
      sequenceIndexMap['a'] = 0;
      sequences['a'] = {
        id: 'sequence_0',
        name: 'Sequence A',
        enabled: true,
        opener: {
          id: 'opener_0',
          text: '',
          enabled: true
        },
        followups: []
      };
    }
    
    // Second pass: add follow-ups to their sequences
    messagesArray.forEach(msg => {
      if (msg.message_type.includes('followup')) {
        // Parse followup1_a (canonical) or followup_1_a (legacy) or followup1/followup_1
        let match = msg.message_type.match(/followup_?(\d+)_([a-z])/i);
        let followupLevel;
        let sequenceId;

        if (match) {
          // Format: followup1_a (or legacy followup_1_a)
          followupLevel = parseInt(match[1]);
          sequenceId = match[2];
        } else {
          // Try format without underscore: followup1 or followup_1
          match = msg.message_type.match(/followup_?(\d+)$/i);
          if (match) {
            followupLevel = parseInt(match[1]);
            // For generic followups, add to first sequence (usually 'a')
            sequenceId = Object.keys(sequences)[0] || 'a';
          }
        }
        
        if (followupLevel && sequenceId) {
          // Make sure the sequence exists
          if (!sequences[sequenceId]) {
            if (!(sequenceId in sequenceIndexMap)) {
              sequenceIndexMap[sequenceId] = sequenceCount++;
            }
            
            sequences[sequenceId] = {
              id: `sequence_${sequenceIndexMap[sequenceId]}`,
              name: `Sequence ${sequenceId.toUpperCase()}`,
              enabled: true,
              opener: {
                id: `opener_${sequenceIndexMap[sequenceId]}`,
                text: '',
                enabled: true
              },
              followups: []
            };
          }
          
          // Add the follow-up
          const seqIndex = sequenceIndexMap[sequenceId];
          sequences[sequenceId].followups.push({
            id: `followup_seq${sequenceId}_lvl${followupLevel}`, // Unique ID format
            text: msg.message_text || '',
            enabled: msg.is_active !== false,
            delay_days: msg.delay_days || followupLevel * 3,
            level: followupLevel
          });
        }
      }
    });
    
    // Convert sequences object to array
    const sequenceArray = Object.values(sequences);
    
    // Sort follow-ups by level/delay days
    sequenceArray.forEach(seq => {
      seq.followups.sort((a, b) => {
        // Sort by level first, then by delay_days
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        return a.delay_days - b.delay_days;
      });
    });
    
    // If no sequences found, create a default one
    if (sequenceArray.length === 0) {
      sequenceArray.push({
        id: 'sequence_0',
        name: 'Sequence A',
        enabled: true,
        opener: {
          id: 'opener_0',
          text: '',
          enabled: true
        },
        followups: []
      });
    }
    
    return {
      messageSequences: sequenceArray
    };
  };

  // Handle step navigation
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  // Form handlers
  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setCampaignData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Message handlers
  const handleMessageDataChange = (newData) => {
    setCampaignData(prev => ({
      ...prev,
      ...newData
    }));
  };

  // Account handlers
  const toggleAccountSelection = async (accountId) => {
    const isCurrentlySelected = campaignData.selectedAccounts.includes(accountId);
    
    // If removing an account, allow it
    if (isCurrentlySelected) {
      setCampaignData(prev => ({
        ...prev,
        selectedAccounts: prev.selectedAccounts.filter(id => id !== accountId)
      }));
      return;
    }
    
    // If adding an account, check subscription limits
    try {
      const canAddResult = await subscriptionService.canAddAccountToCampaign();
      
      if (!canAddResult.allowed) {
        // Show upgrade modal
        setUpgradeModalContent({
          title: 'Account Limit Reached',
          message: canAddResult.message,
          tier: canAddResult.tier,
          currentUsage: canAddResult.currentUsage,
          limit: canAddResult.limit,
          upgradeRequired: canAddResult.upgradeRequired,
          canIncreaseQuantity: canAddResult.canIncreaseQuantity
        });
        setShowUpgradeModal(true);
        return;
      }
      
      // Check if we're at the limit after adding this account
      const newCount = campaignData.selectedAccounts.length + 1;
      if (canAddResult.limit !== 'unlimited' && newCount > canAddResult.limit) {
        setUpgradeModalContent({
          title: 'Account Limit Reached',
          message: `You can only add ${canAddResult.limit} account${canAddResult.limit > 1 ? 's' : ''} to campaigns with your current plan.`,
          tier: canAddResult.tier,
          currentUsage: canAddResult.currentUsage,
          limit: canAddResult.limit,
          upgradeRequired: canAddResult.tier === 'starter',
          canIncreaseQuantity: canAddResult.tier === 'growth'
        });
        setShowUpgradeModal(true);
        return;
      }
      
      // Allow the account to be added
      setCampaignData(prev => ({
        ...prev,
        selectedAccounts: [...prev.selectedAccounts, accountId]
      }));
      
    } catch (error) {
      console.error('Error checking subscription limits:', error);
      alert('Failed to check subscription limits. Please try again.');
    }
  };

  // Validation
  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Basic Info
        return campaignData.name.trim() && campaignData.description.trim();
      case 1: // Leads Lists
        return campaignData.selectedLeadsLists.length > 0;
      case 2: // Messages
        return campaignData.messageSequences && 
               campaignData.messageSequences.length > 0 && 
               campaignData.messageSequences[0].opener.text.trim();
      case 3: // Accounts
        return campaignData.selectedAccounts.length > 0;
      case 4: // Scheduling
        return campaignData.send_time_start && campaignData.send_time_end;
      case 5: // Review
        return true;
      default:
        return false;
    }
  };

  // Handle leads list selection (multiple lists)
  const handleLeadsListSelected = (leadsList) => {
    setCampaignData(prev => ({
      ...prev,
      selectedLeadsLists: prev.selectedLeadsLists.find(list => list.id === leadsList.id)
        ? prev.selectedLeadsLists // Already selected, don't add again
        : [...prev.selectedLeadsLists, leadsList] // Add to array
    }));
    setShowLeadsImport(false);
  };

  // Remove leads list
  const handleRemoveLeadsList = (listId) => {
    setCampaignData(prev => ({
      ...prev,
      selectedLeadsLists: prev.selectedLeadsLists.filter(list => list.id !== listId)
    }));
  };

  // Submit campaign updates
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Update the campaign basic info
      await campaignService.updateCampaign(campaign.id, {
        name: campaignData.name,
        description: campaignData.description,
        daily_message_min: campaignData.daily_message_min,
        daily_message_max: campaignData.daily_message_max,
        send_time_start: campaignData.send_time_start + ':00',
        send_time_end: campaignData.send_time_end + ':00',
        timezone: campaignData.timezone,
        exclude_weekends: campaignData.exclude_weekends,
        exclude_holidays: campaignData.exclude_holidays,
        automation_settings: campaignData.automation_settings
      });

      // Delete and recreate campaign messages
      const existingMessages = await campaignMessageService.getCampaignMessages(campaign.id);
      for (const msg of existingMessages) {
        await campaignMessageService.deleteCampaignMessage(msg.id);
      }

      // Convert sequences back to messages for saving
      if (campaignData.messageSequences && campaignData.messageSequences.length > 0) {
        const messagesToSave = [];
        
        campaignData.messageSequences.forEach((sequence, seqIndex) => {
          if (sequence.enabled && sequence.opener.text) {
            // Save opener
            messagesToSave.push({
              campaign_id: campaign.id,
              message_type: `opener_${String.fromCharCode(97 + seqIndex)}`, // a, b, c, etc.
              message_text: sequence.opener.text,
              is_active: sequence.opener.enabled !== false,
              delay_days: 0
            });
            
            // Save followups
            sequence.followups.forEach((followup, followupIndex) => {
              if (followup.enabled && followup.text) {
                messagesToSave.push({
                  campaign_id: campaign.id,
                  // Must match what task-completion chains on: followup{N}_{variant}
                  // (NO underscore before the number) — e.g. followup1_a, followup2_b.
                  message_type: `followup${followupIndex + 1}_${String.fromCharCode(97 + seqIndex)}`,
                  message_text: followup.text,
                  is_active: followup.enabled !== false,
                  delay_days: followup.delay_days || (followupIndex + 1) * 3
                });
              }
            });
          }
        });
        
        // Batch insert all messages
        for (const msg of messagesToSave) {
          await campaignMessageService.addCampaignMessage(msg);
        }
      }
      
      // Update campaign accounts
      const existingAccounts = await campaignAccountService.getCampaignAccounts(campaign.id);
      
      // Remove accounts that are no longer selected
      for (const existingAccount of existingAccounts) {
        if (!campaignData.selectedAccounts.includes(existingAccount.instagram_account_id)) {
          await campaignAccountService.removeAccountFromCampaign(campaign.id, existingAccount.instagram_account_id);
        }
      }
      
      // Add new accounts that are selected but not yet added
      const existingAccountIds = existingAccounts.map(ea => ea.instagram_account_id);
      for (const accountId of campaignData.selectedAccounts) {
        if (!existingAccountIds.includes(accountId)) {
          await campaignAccountService.addAccountToCampaign(campaign.id, accountId);
        }
      }
      
      // Update campaign leads lists
      const existingLeadsLists = await campaignLeadsListService.getCampaignLeadsLists(campaign.id);
      
      // Remove leads lists that are no longer selected
      for (const existingList of existingLeadsLists) {
        if (!campaignData.selectedLeadsLists.find(list => list.id === existingList.leads_list_id)) {
          await campaignLeadsListService.removeLeadsListFromCampaign(campaign.id, existingList.leads_list_id);
        }
      }
      
      // Add new leads lists that are selected but not yet added
      const existingListIds = existingLeadsLists.map(el => el.leads_list_id);
      const newListIds = campaignData.selectedLeadsLists
        .filter(list => !existingListIds.includes(list.id))
        .map(list => list.id);
      
      if (newListIds.length > 0) {
        await campaignLeadsListService.addLeadsListsToCampaign(campaign.id, newListIds);
      }
      
      onCampaignUpdated?.(campaign.id);
      onClose();
      
    } catch (error) {
      console.error('Error updating campaign:', error);
      alert('Failed to update campaign: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal close handler
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setCurrentStep(0);
    }
  };

  if (!isOpen || !campaign) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`${
            darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
          } rounded shadow-2xl border max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-b flex items-center justify-between`}>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center space-x-2`}>
                <Edit3 className="w-6 h-6" />
                <span>Edit Campaign</span>
              </h2>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mt-1`}>
                {campaign.name} • {steps[currentStep].description}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className={`p-2 ${darkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded transition-colors`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Step Progress - Fixed Layout */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-b`}>
            <div className="grid grid-cols-6 gap-3">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                const isClickable = index <= currentStep || isStepValid(index);

                return (
                  <button
                    key={step.id}
                    onClick={() => isClickable && goToStep(index)}
                    disabled={!isClickable}
                    className={`flex flex-col items-center p-3 rounded transition-all h-20 ${
                      isActive
                        ? darkMode
                          ? 'bg-gradient-to-b from-cyan-500/20 to-blue-600/20 text-cyan-400 border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20'
                          : 'bg-gradient-to-b from-blue-500/20 to-purple-600/20 text-blue-600 border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
                        : isCompleted
                        ? darkMode
                          ? 'bg-gradient-to-b from-emerald-500/20 to-green-600/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-gradient-to-b from-green-500/20 to-emerald-600/20 text-green-600 border border-green-500/30'
                        : isClickable
                        ? darkMode
                          ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 border border-slate-600/50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-300'
                        : darkMode
                        ? 'text-slate-600 border border-slate-700'
                        : 'text-gray-300 border border-gray-200'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center mb-1 ${
                      isActive
                        ? darkMode
                          ? 'bg-cyan-500/30'
                          : 'bg-blue-500/30'
                        : isCompleted
                        ? darkMode
                          ? 'bg-emerald-500/30'
                          : 'bg-green-500/30'
                        : darkMode
                        ? 'bg-slate-600/30'
                        : 'bg-gray-300/30'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Icon className="w-3 h-3" />
                      )}
                    </div>
                    <span className="text-xs font-medium text-center leading-tight">
                      {step.title}
                    </span>
                    <div className={`w-full h-1 mt-1 rounded-full ${
                      isActive
                        ? darkMode
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600'
                          : 'bg-gradient-to-r from-blue-500 to-purple-600'
                        : isCompleted
                        ? darkMode
                          ? 'bg-gradient-to-r from-emerald-500 to-green-600'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600'
                        : darkMode
                        ? 'bg-slate-600'
                        : 'bg-gray-300'
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className={`w-8 h-8 border-2 ${darkMode ? 'border-cyan-500' : 'border-blue-500'} border-t-transparent rounded-full animate-spin`} />
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Step content will be rendered here based on currentStep */}
                  {currentStep === 0 && (
                    <CampaignBasicInfo 
                      data={campaignData}
                      onChange={handleInputChange}
                      darkMode={darkMode}
                    />
                  )}
                  {currentStep === 1 && (
                    <LeadsListSelection
                      data={campaignData}
                      onSelectLeadsList={handleLeadsListSelected}
                      onRemoveLeadsList={handleRemoveLeadsList}
                      onShowImport={() => setShowLeadsImport(true)}
                      darkMode={darkMode}
                    />
                  )}
                  {currentStep === 2 && (
                    <MessageSequencesFull
                      data={campaignData}
                      onDataChange={handleMessageDataChange}
                      darkMode={darkMode}
                    />
                  )}
                  {currentStep === 3 && (
                    <AccountSelection
                      data={campaignData}
                      accounts={availableAccounts}
                      userProfile={userProfile}
                      onToggleAccount={toggleAccountSelection}
                      darkMode={darkMode}
                    />
                  )}
                  {currentStep === 4 && (
                    <SchedulingSettings
                      data={campaignData}
                      onChange={handleInputChange}
                      darkMode={darkMode}
                    />
                  )}
                  {currentStep === 5 && (
                    <ReviewAndLaunch
                      data={campaignData}
                      accounts={availableAccounts}
                      darkMode={darkMode}
                      isEdit={true}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* Footer */}
          <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-t flex items-center justify-between`}>
            <button
              onClick={prevStep}
              disabled={currentStep === 0 || isSubmitting || isLoading}
              className={`flex items-center space-x-2 px-4 py-2 rounded transition-colors ${
                currentStep === 0 || isSubmitting || isLoading
                  ? darkMode ? 'text-slate-600' : 'text-gray-300'
                  : darkMode ? 'text-slate-300 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex space-x-3">
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={nextStep}
                  disabled={!isStepValid(currentStep) || isSubmitting || isLoading}
                  className={`flex items-center space-x-2 px-6 py-2 rounded font-medium transition-colors ${
                    !isStepValid(currentStep) || isSubmitting || isLoading
                      ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'
                      : darkMode ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <span>Continue</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid(currentStep) || isSubmitting || isLoading}
                  className={`flex items-center space-x-2 px-6 py-2 rounded font-medium transition-colors ${
                    !isStepValid(currentStep) || isSubmitting || isLoading
                      ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'
                      : darkMode ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white' : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Update Campaign</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Upgrade Modal */}
        <AnimatePresence>
          {showUpgradeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
              onClick={() => setShowUpgradeModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-md ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} rounded p-6 border shadow-xl`}
              >
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${
                    upgradeModalContent.tier === 'free' 
                      ? 'bg-blue-100 dark:bg-blue-500/20' 
                      : 'bg-amber-100 dark:bg-amber-500/20'
                  } flex items-center justify-center`}>
                    <AlertCircle className={`w-8 h-8 ${
                      upgradeModalContent.tier === 'free' 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-amber-600 dark:text-amber-400'
                    }`} />
                  </div>
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                    {upgradeModalContent.title}
                  </h3>
                  <p className={`${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
                    {upgradeModalContent.message}
                  </p>
                  
                  {/* Usage Stats */}
                  {upgradeModalContent.limit !== undefined && (
                    <div className={`p-3 rounded ${darkMode ? 'bg-slate-900/50' : 'bg-gray-50'} mb-4`}>
                      <div className="flex justify-between items-center text-sm">
                        <span className={darkMode ? 'text-slate-400' : 'text-gray-600'}>
                          Current Usage
                        </span>
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {upgradeModalContent.currentUsage} of {upgradeModalContent.limit === 'unlimited' ? '∞' : upgradeModalContent.limit}
                        </span>
                      </div>
                      {upgradeModalContent.limit !== 'unlimited' && upgradeModalContent.limit > 0 && (
                        <div className={`w-full h-2 rounded-full mt-2 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                          <div 
                            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                            style={{ 
                              width: `${Math.min((upgradeModalContent.currentUsage / upgradeModalContent.limit) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-3">
                  {upgradeModalContent.upgradeRequired ? (
                    <button
                      onClick={() => {
                        setShowUpgradeModal(false);
                        window.location.href = '/pricing';
                      }}
                      className={`w-full py-3 px-4 rounded font-medium text-white ${
                        darkMode 
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' 
                          : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                      } transition-all duration-200 shadow-lg`}
                    >
                      Upgrade Plan
                    </button>
                  ) : upgradeModalContent.canIncreaseQuantity ? (
                    <>
                      <button
                        onClick={() => {
                          setShowUpgradeModal(false);
                          window.location.href = '/pricing';
                        }}
                        className={`w-full py-3 px-4 rounded font-medium text-white ${
                          darkMode 
                            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' 
                            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                        } transition-all duration-200 shadow-lg`}
                      >
                        Add More Accounts
                      </button>
                      <button
                        onClick={() => {
                          setShowUpgradeModal(false);
                          window.location.href = '/pricing';
                        }}
                        className={`w-full py-2 px-4 rounded font-medium ${
                          darkMode 
                            ? 'text-slate-300 hover:text-white border border-slate-600 hover:border-slate-500' 
                            : 'text-gray-700 hover:text-gray-900 border border-gray-300 hover:border-gray-400'
                        } transition-all duration-200`}
                      >
                        Upgrade to Scale (Unlimited)
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setShowUpgradeModal(false);
                        window.location.href = '/pricing';
                      }}
                      className={`w-full py-3 px-4 rounded font-medium text-white ${
                        darkMode 
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' 
                          : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                      } transition-all duration-200 shadow-lg`}
                    >
                      View Pricing
                    </button>
                  )}
                  
                  <button
                    onClick={() => setShowUpgradeModal(false)}
                    className={`w-full py-2 px-4 rounded font-medium ${
                      darkMode 
                        ? 'text-slate-400 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    } transition-colors`}
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Leads Import Modal */}
        <LeadsImportModal
          isOpen={showLeadsImport}
          onClose={() => setShowLeadsImport(false)}
          onLeadsListSelected={handleLeadsListSelected}
          darkMode={darkMode}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Step Components (reusing from CampaignWizard)
const CampaignBasicInfo = ({ data, onChange, darkMode }) => (
  <div className="space-y-6">
    <div>
      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>
        Campaign Information
      </h3>
      <div className="space-y-4">
        <div>
          <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
            Campaign Name *
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g., Real Estate Lead Generation Q1"
            className={`w-full px-4 py-3 border rounded focus:ring-2 focus:border-transparent ${
              darkMode 
                ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
            }`}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
            Description *
          </label>
          <textarea
            value={data.description}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Describe your campaign goals and target audience..."
            rows={3}
            className={`w-full px-4 py-3 border rounded focus:ring-2 focus:border-transparent ${
              darkMode 
                ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
            }`}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'} mb-2`}>
              Industry
            </label>
            <select
              value={data.industry}
              onChange={(e) => onChange('industry', e.target.value)}
              className={`w-full px-4 py-3 border rounded focus:ring-2 focus:border-transparent ${
                darkMode 
                  ? 'bg-slate-800 border-slate-600 text-white focus:ring-cyan-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              }`}
            >
              <option value="">Select Industry</option>
              <option value="real_estate">Real Estate</option>
              <option value="fitness">Fitness & Health</option>
              <option value="coaching">Coaching</option>
              <option value="ecommerce">E-commerce</option>
              <option value="saas">SaaS</option>
              <option value="consulting">Consulting</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default CampaignEditor;